import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { actions } from '../../store/recipientQueryTemplate';
import { produce } from 'immer';
import FormController from '../../common/formController';
import Joi from 'joi';
import validators from './validator';
import { ProductType } from 'constants.js';
import { getSchemas } from 'store/schema/actions';
import {
  getRecipientCountRequestId,
  getRecipientDetails,
  resetListRecipientCount
} from 'store/recipientQueryTemplate/actions';
import { shouldPreserveCopyTemplateState } from 'helpers/templateCopyGuard';

const {
  getQueryTemplate,
  setCurrentQueryTemplate,
  updateQueryTemplate,
  createQueryTemplate,
  setNextStep,
  unsetNextStep,
  setCopyCurrentQueryTemplate
} = actions;

let formController = new FormController();

export default function useTemplate(nextPath, backPath) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { templateUuid: originalQueryId } = useParams();
  const template = useSelector(store => store.queryTemplate.current);
  const isLoading = useSelector(store => store.queryTemplate.getting);
  const isInTemplateDesignDialog = useSelector(
    store => store.queryTemplate.isInTemplateDesignDialog
  );
  const isTemplateSaving = useSelector(
    store => store.queryTemplate.isTemplateSaving
  );
  const isTemplateSaved = useSelector(
    store => store.queryTemplate.isTemplateSaved
  );
  const productIntegrationOptions = useSelector(
    store => store.events.supportedEventTypes.productIntegrationOptions
  );
  const supportedOnDemandTypeOptions = useSelector(
    store => store.events.supportedOnDemandTypes
  );
  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );
  const listManagementSchema = useSelector(
    store => store.schema.listManagementSchema
  );
  const guestInsightSchema = useSelector(
    store => store.schema.guestInsightSchema
  );
  const [validationErrors, setValidationErrors] = useState({});
  const [errorValidationSegment, setErrorValidationSegment] = useState(null);
  const [queryId, setQueryId] = useState(originalQueryId);
  const [thisTemplate, setThisTemplate] = useState({
    type: '',
    name: ''
  });

  const validate = (saveAttempted = false, template) => {
    let segment = errorValidationSegment;
    const joiValidators = Joi.object(validators[segment]);
    const validation = joiValidators.validate(template || thisTemplate, {
      abortEarly: false,
      allowUnknown: true // allow unknown keys that will be ignored
    });
    let errors = formController.getFormErrors(validation, { saveAttempted });
    setValidationErrors(errors);
    return errors;
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!isLoading) {
      const queryParams = new URLSearchParams(location.search);
      const businessContextId = queryParams.get('businessContextId');

      dispatch(getQueryTemplate({
        templateId: queryId || template.uuid,
        ...(businessContextId && { businessContextId })
      }));
    }
    if (Object.keys(template).length > 0) {
      formController = new FormController();
      setThisTemplate({ ...template });
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const copy = queryParams.get('copy');
    
    if (copy === 'true') {
      setQueryId(undefined);
      
      let queryToCopy = Object.assign(
        {},
        template
      );
      if (queryToCopy && queryToCopy.uuid) {
        delete queryToCopy.id;
        delete queryToCopy.uuid;
        delete queryToCopy.defaultTemplate;
        queryToCopy._isCopy = true;
        queryToCopy._isCopyTemplate = true;
        queryToCopy.name = queryToCopy.name + `-copy`;
        queryToCopy.active = true;
        queryToCopy.schemaIdentifier = queryToCopy.schemaIdentifier
          ? queryToCopy.schemaIdentifier
          : queryToCopy.productCategory
            ? `${queryToCopy.productCategory}.${queryToCopy.productName}.${queryToCopy.eventTypeCategory}`
            : '';
        // Update businessContextId for copied queries
        queryToCopy.businessContextId = currentBusinessContextId;
  
        setThisTemplate(prevTemplate => ({
          ...queryToCopy,
          hasMovedToNextStep: prevTemplate.hasMovedToNextStep || false
        }));
        
        const newUrl = '/recipient-queries/details';
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.search, template.uuid, setCopyCurrentQueryTemplate]);

  useEffect(() => {
    if (
      productIntegrationOptions?.filter(option => option != 'itinerary')
        .length === 1 &&
      !thisTemplate.hasOwnProperty('productName') &&
      thisTemplate.productType !== ProductType.List &&
      thisTemplate.productType !== ProductType.GUEST_INSIGHTS
    ) {
      handleAutomationChange({
        target: { name: 'productName', value: productIntegrationOptions[0] }
      });
    }
  }, [thisTemplate.productType, thisTemplate.productName, productIntegrationOptions]);

  useEffect(() => {
    if (Object.keys(template).length > 0) {
      formController = new FormController();
      const queryParams = new URLSearchParams(location.search);
      const isCopyMode = queryParams.get('copy') === 'true';

      setThisTemplate(prevState => {
        const isUnsavedCopyFlow = shouldPreserveCopyTemplateState({
          isCopyMode,
          previousTemplate: prevState,
          incomingTemplate: template
        });

        if (isUnsavedCopyFlow) {
          return prevState;
        }

        if (!queryId) {
          return {
            ...prevState,
            ...template,
            hasMovedToNextStep: template.hasMovedToNextStep || false
          };
        }

        return { ...prevState, ...template };
      });
    }
  }, [template, location]);

  useEffect(() => {
    if (
      thisTemplate.productType &&
      thisTemplate.productType === ProductType.List
    ) {
      setThisTemplate({
        ...thisTemplate,
        productCategory: listManagementSchema?.eventProductCategory || '',
        productName: listManagementSchema?.eventProductName || '',
        eventTypeCategory: listManagementSchema?.eventTypeCategory || '',
        schemaIdentifier:
          listManagementSchema?.eventProductCategory +
            '.' +
            listManagementSchema?.eventProductName +
            '.' +
            listManagementSchema?.eventTypeCategory || '',
        partialCollectionName:
          listManagementSchema?.schemaToEventPartialCollectionName || ''
      });
    }
  }, [listManagementSchema]);

  useEffect(() => {
    if (
      thisTemplate.productType &&
      thisTemplate.productType === ProductType.GUEST_INSIGHTS
    ) {
      setThisTemplate({
        ...thisTemplate,
        productCategory: guestInsightSchema?.eventProductCategory || '',
        productName: guestInsightSchema?.eventProductName || '',
        eventTypeCategory: guestInsightSchema?.eventTypeCategory || '',
        schemaIdentifier:
          guestInsightSchema?.eventProductCategory +
            '.' +
            guestInsightSchema?.eventProductName +
            '.' +
            guestInsightSchema?.eventTypeCategory || '',
        partialCollectionName:
        guestInsightSchema?.schemaToEventPartialCollectionName || ''
      });
    }
  }, [guestInsightSchema]);

  const goNext = event => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(
        setCurrentQueryTemplate({
          ...thisTemplate,
          hasMovedToNextStep: true
        })
      );
      formController = new FormController();
      if (
        nextPath ||
        (event.currentTarget.attributes.link &&
          event.currentTarget.attributes.link.value)
      ) {
        history.push(
          `${nextPath || event.currentTarget.attributes.link.value}${
            (queryId && !(thisTemplate._isCopy || thisTemplate._isCopyTemplate)) ? `/${queryId}` : ''
          }`
        );
      } else {
        setThisTemplate({
          ...thisTemplate,
          hasMovedToNextStep: true
        })
        dispatch(setNextStep());
      }
    }
  };
  const resetValidationErrors = () => {
    setValidationErrors({});
    setThisTemplate({
      type: '',
      name: ''
    });
  };

  const goBack = event => {
    dispatch(setCurrentQueryTemplate({ ...thisTemplate }));
    if (
      backPath ||
      (event.currentTarget.attributes.link &&
        event.currentTarget.attributes.link.value)
    ) {
      let id = queryId ? `/${queryId}` : '';
      history.push(
        `${backPath || event.currentTarget.attributes.link.value}${id}`
      );
    } else {
      dispatch(unsetNextStep());
    }
  };

  const navBackOnError = path => {
    if (
      thisTemplate.hasOwnProperty('hasMovedToNextStep') &&
      !thisTemplate.hasMovedToNextStep &&
      !thisTemplate.name
    ) {
      dispatch(
        setCurrentQueryTemplate({
          ...thisTemplate,
          hasMovedToNextStep: false
        })
      );
      history.push(path);
    }
  };

  const onTabChange = () => {
    dispatch(setCurrentQueryTemplate(thisTemplate));
  };

  // setByPath({}, 'key.key', 123);
  const setByPath = (obj, path, value) => {
    if (typeof path === 'string') {
      path = path.split('.');
    }
    if (obj && path.length === 1) {
      obj[path] = value;
      return;
    }
    return setByPath(obj[path[0]], path.slice(1), value);
  };

  const handleChange = events => {
    events = Array.isArray(events) ? events : [events];

    if (events.length > 0) {
      let templateUpdate = { ...thisTemplate };
      events.forEach(event => {
        setByPath(templateUpdate, event.target.name, event.target.value);
      });
      dispatch(resetListRecipientCount());
      setThisTemplate(templateUpdate);
      validate(false, templateUpdate);
    }
  };

  const toggleEventSegmentation = () => {
    setThisTemplate({
      ...thisTemplate,
      eventSegmentation: {
        segmentAgainstEvent: !thisTemplate.eventSegmentation?.segmentAgainstEvent,
        productSegmentation: "ALL"
      }
    });
  };

  const handleProductSegmentationChange = event => {
    setThisTemplate({
      ...thisTemplate,
      eventSegmentation: {
        ...thisTemplate.eventSegmentation,
        productSegmentation: event?.value
      }
    });
    validate(false, thisTemplate);
  };

  const handleAutomationChange = event => {
    const { name, value } = event.target;
    let typeOption = {};
    let templateUpdate = produce(thisTemplate, draft => {
      typeOption = supportedOnDemandTypeOptions.find(
        option => option.id === value
      );

      draft.productCategory = typeOption?.productCategory || '';
      draft.productName = typeOption?.productName || '';
      draft.eventTypeCategory = typeOption?.eventTypeCategory || '';
      draft.schemaIdentifier = typeOption?.schemaIdentifier || '';
      draft.schemaId = typeOption?.schemaId || '';
      draft.partialCollectionName = typeOption?.partialCollectionName || '';
      setByPath(draft, name, value);
    });
    setThisTemplate(templateUpdate);
    validate(false, templateUpdate);
  };

  const handleRecipientCount = event => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(getRecipientCountRequestId({ ...thisTemplate }));
    }
  };

  const handleRecipientDetails = event => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(getRecipientDetails({ ...thisTemplate }));
      return true;
    }
    return false;
  };

  const saveTemplate = () => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(setCurrentQueryTemplate({ ...thisTemplate }));
      if (thisTemplate.uuid) {
        dispatch(updateQueryTemplate());
      } else {
        dispatch(createQueryTemplate());
      }
      formController = new FormController();
    }
  };

  const onBlur = e => {
    formController.onBlur(e);
    validate();
  };

  const onFocus = e => {
    formController.onFocus(e);
    validate();
  };

  const isQueryReadOnly = () => {
    return (
      thisTemplate.uuid &&
      currentBusinessContextId !== thisTemplate.businessContextId
    );
  };

  const closeQuery = () => {
    formController = new FormController();
    history.push('/recipient-queries');
  };

  const shouldShowError = name => {
    return formController.shouldShowError(validationErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(validationErrors, name);
  };

  const showFormSaveError = () => {
    return formController.showFormSaveError(validationErrors);
  };

  const updateCurrentErrorValidationSegment = name => {
    setErrorValidationSegment(name);
  };

  const handleTypeChange = event => {
    if (event.target.value === ProductType.List || event.target.value === ProductType.GUEST_INSIGHTS) {
      dispatch(getSchemas({ showActive: true }));
    }
    setThisTemplate({
      ...thisTemplate,
      productType: event.target.value,
      eventType: '',
      productCategory: '',
      productName: '',
      eventTypeCategory: '',
      schemaIdentifier: '',
      subscriberListId: '',
      segmentedListId: '',
      ...(ProductType.List && { 
        eventSegmentation: {
          segmentAgainstEvent: false,
          productSegmentation: "ALL"
        }
      })
    });
  };

  const handleSubscriberListChange = (event, option) => {
    if (!option) {
      setThisTemplate({
        ...thisTemplate,
        subscriberListId: '',
        segmentedListId: ''
      });
      return;
    }
    setThisTemplate({
      ...thisTemplate,
      ...(option.listId
        ? { subscriberListId: option.listId, segmentedListId: option.id }
        : { subscriberListId: option.id, segmentedListId: '' })
    });
  };

  return {
    thisTemplate,
    validationErrors,
    handleChange,
    handleAutomationChange,
    toggleEventSegmentation,
    handleProductSegmentationChange,
    handleRecipientCount,
    goNext,
    goBack,
    navBackOnError,
    onTabChange,
    saveTemplate,
    onFocus,
    onBlur,
    isQueryReadOnly,
    closeQuery,
    showFormSaveError,
    getErrorMessage,
    shouldShowError,
    updateCurrentErrorValidationSegment,
    supportedOnDemandTypeOptions,
    productIntegrationOptions,
    isLoading,
    isTemplateSaving,
    isInTemplateDesignDialog,
    resetValidationErrors,
    isTemplateSaved,
    handleTypeChange,
    handleSubscriberListChange,
    handleRecipientDetails
  };
}
